import { DECOY_HIT_SCORE } from '../data/boss';
import type { LevelId } from '../data/ids';
import {
  ARMOR_DUPLICATE_BONUS,
  BOSS_POINTS,
  CHECKPOINT_POINTS,
  CLEAN_RUN_POINTS,
  COLUMN_POINTS,
  DOLLAR_POINTS,
  JUMP_GRADE_POINTS,
  LANDING_POINTS,
  type JumpGrade,
} from '../data/tuning';
import type { LevelEnding } from '../data/types';
import { t } from '../i18n/es';

/**
 * What a completed level scored, counted from the events of the core while it was flown (issue #62): the
 * recount never adds anything the core did not score, so its lines always sum the level score.
 */
export interface LevelResult {
  levelId: LevelId;
  ending: LevelEnding;
  columns: number;
  dollars: number;
  checkpoints: number;
  /** Armor picked up while armored (GDD §7). */
  armorBonuses: number;
  /** Decoys that hit the boss (GDD §9.1, #90). */
  decoyHits: number;
  /** Silver coins of the boss attacks (GDD §12, #95): how many and their points. */
  coins?: number;
  coinPoints?: number;
  /** Landed on the airstrip with its bonus (#72); false while the simple finish line stands. */
  landed: boolean;
  bossDefeated: boolean;
  jump: JumpGrade | null;
  cleanRun: boolean;
  scoreAtLevelStart: number;
  /** Best level score before this completion (GDD §12), or null the first time. */
  previousBest: number | null;
  /** The level was flown with the assist mode on (#138), and whether the best score before was an unassisted one. */
  assisted?: boolean;
  previousUnassisted?: boolean;
}

export interface RecountLine {
  label: string;
  /** How many of them, for the counted lines (columns, bills, checkpoints, armor). */
  count: number | null;
  points: number;
}

export interface Recount {
  levelId: LevelId;
  stamp: string;
  lines: RecountLine[];
  levelTotal: number;
  runTotal: number;
  newRecord: boolean;
  /** Flown with the assist mode on (GDD §13.6, #138): the screen marks it ASISTIDO. */
  assisted: boolean;
}

/** The recount lines of GDD §13.5, with the score table of §12. */
export function buildRecount(result: LevelResult): Recount {
  const counted = (label: string, count: number, each: number): RecountLine => ({ label, count, points: count * each });
  const bonus = (label: string, points: number): RecountLine => ({ label, count: null, points });
  const lines: RecountLine[] = [
    counted(t('ui.recuento.line.columns'), result.columns, COLUMN_POINTS),
    counted(t('ui.recuento.line.dollars'), result.dollars, DOLLAR_POINTS),
    counted(t('ui.recuento.line.checkpoints'), result.checkpoints, CHECKPOINT_POINTS),
  ];
  if (result.armorBonuses > 0) lines.push(counted(t('ui.recuento.line.armor'), result.armorBonuses, ARMOR_DUPLICATE_BONUS));
  if ((result.coins ?? 0) > 0) lines.push({ label: t('ui.recuento.line.coins'), count: result.coins!, points: result.coinPoints ?? 0 });
  if (result.decoyHits > 0) lines.push(counted(t('ui.recuento.line.decoys'), result.decoyHits, DECOY_HIT_SCORE));
  if (result.bossDefeated) lines.push(bonus(t('ui.recuento.line.boss'), BOSS_POINTS));
  if (result.ending === 'airstrip') lines.push(bonus(t('ui.recuento.line.landing'), result.landed ? LANDING_POINTS : 0));
  else if (result.jump) lines.push(bonus(t('ui.recuento.line.jump', { grade: result.jump }), JUMP_GRADE_POINTS[result.jump]));
  // Shown with 0 when lives were lost, so the pilot knows it exists.
  lines.push(bonus(t('ui.recuento.line.clean'), result.cleanRun ? CLEAN_RUN_POINTS : 0));

  const levelTotal = lines.reduce((sum, line) => sum + line.points, 0);
  return {
    levelId: result.levelId,
    stamp: t(result.ending === 'airstrip' ? 'ui.recuento.stamp.landed' : 'ui.recuento.stamp.delivered'),
    lines,
    levelTotal,
    runTotal: result.scoreAtLevelStart + levelTotal,
    // An assisted run never replaces an unassisted record, so it is not a new mark (#138).
    newRecord: result.previousBest === null || (levelTotal > result.previousBest && !(result.assisted && result.previousUnassisted)),
    assisted: result.assisted ?? false,
  };
}
